import * as React from "react";
import PaginatedList from "./PaginatedList";
import InvitationDialog from "./commons/components/InvitationDialog";
import Page from "./commons/components/pagination/Page";
import Identity from "./commons/model/Identity";
import IdentityText from "./commons/components/IdentityText";
import ImpersonateAction from "./commons/model/ImpersonateAction";
import Column from "./commons/components/pagination/Column";
import HeaderBar from "./commons/components/HeaderBar";
import SubscriptionPlan from "./commons/model/pricing/SubscriptionPlan";
import AsyncAction from "./AsyncAction";
import sfetch from "./commons/utils/super-fetch";
import { PatientInformation } from "./commons/model/patient/PatientInfo";
import Country from "./commons/model/address/Country";
import { createRoot } from "react-dom/client";

interface UserListProps {
  canInvite: boolean;
  canList: boolean;
  countries?: Country[];
  showExternalUserId: boolean;
  showCountry: boolean;
  impersonateAction: ImpersonateAction;
  inviteEndpoint?: string;
  page: Page<UserWithPaymentContactInfo>;
  columns: Column[];
  userType: string;
  fetchUrl: string;
  profileUrl: string;
  subscriptionPlansEndpoint?: string;
}

export interface UserWithPaymentContactInfo extends PatientInformation {
  subscription: any;
}

interface UserListState {
  inviteDialogVisible: boolean;
  plans?: SubscriptionPlan[];
  defaultPlanId?: string;
}

export default class UserList extends React.Component<UserListProps, UserListState> {
  constructor(props: UserListProps) {
    super(props);
    this.state = {
      inviteDialogVisible: false,
      plans: null,
      defaultPlanId: null,
    };
    this.showInviteDialog = this.showInviteDialog.bind(this);
  }

  render() {
    const {
      canInvite,
      canList,
      page,
      userType,
      impersonateAction,
      columns,
      subscriptionPlansEndpoint,
    } = this.props;
    columns.find((it) => it.path === "fullName").renderer = (item) => (
      <IdentityText identity={item} impersonateAction={impersonateAction} />
    );
    const activeSubscriptionColumn = columns.find((it) => it.path === "subscription.active");
    if (activeSubscriptionColumn) {
      activeSubscriptionColumn.renderer = (user?: UserWithPaymentContactInfo) => {
        if (user.subscription.active == null) return null;
        else if (user.subscription.active) return <i className="ic_check" />;
        else return <i className="ic_close" />;
      };
    }

    const inviteButton = canInvite && (
      <AsyncAction
        buttonId="invite-button"
        promise={() => this.showInviteDialog(!!subscriptionPlansEndpoint)}
        disabled={false}
        buttonLabel={i18n["Global.Invite"]}
      />
    );

    return (
      <>
        <HeaderBar title={i18n[`Page.${userType}.Title`]} action={inviteButton} />
        {canList && (
          <div className="paginated-list-container">
            <PaginatedList
              initialPage={page}
              emptyListMessage={i18n[`Page.${userType}.NoUser`]}
              itemKey="email"
              columns={this.props.columns}
              fetchUrl={this.props.fetchUrl}
              rowOnClick={this.onUserClick()}
              classNames="with-shadow"
            />
          </div>
        )}
        {this.state.inviteDialogVisible && (
          <InvitationDialog
            {...this.props}
            plans={this.state.plans}
            hasExternalUserId={this.props.showExternalUserId}
            defaultPlanId={this.state.defaultPlanId}
            inviteEndpoint={this.props.inviteEndpoint}
            onCancel={() => this.hideInviteDialog()}
            onClose={UserList.reload}
            title={i18n[`Dialog.Invite.${userType}.Title`]}
            submitLabel={i18n["Dialog.Invite.Submit"]}
          />
        )}
      </>
    );
  }

  private onUserClick() {
    const profileUrl = this.props.profileUrl;
    return (identity: Identity) => {
      document.location.href = `${profileUrl}${identity.uid}`;
    };
  }

  private async showInviteDialog(withPlans: boolean): Promise<any> {
    if (withPlans) {
      const response = await sfetch(this.props.subscriptionPlansEndpoint, {
        method: "GET",
      });
      const json = await response.json();
      this.setState(json);
    }

    this.setState({ inviteDialogVisible: true });
  }

  private hideInviteDialog() {
    this.setState({ inviteDialogVisible: false });
  }

  private static reload() {
    document.location.reload();
  }

  static loadComponent(elementId: string, props: UserListProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<UserList {...props} />);
  }
}
